import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2, AlertCircle } from "lucide-react";
import { useI18n } from "@/i18n/I18nContext";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const isValidEmail = (email) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email).toLowerCase());

const EmailForm = () => {
  const { t, lang, dir } = useI18n();
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading | success | already | error
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!isValidEmail(email)) {
      setStatus("error");
      toast.error(t.hero.cta_error);
      return;
    }
    setLoading(true);
    setStatus("loading");
    try {
      const { data } = await axios.post(`${API}/subscribe`, {
        email,
        locale: lang,
      });
      if (data.already_subscribed) {
        setStatus("already");
        toast(t.hero.cta_already, { icon: "✨" });
      } else {
        setStatus("success");
        toast.success(t.hero.cta_subscribed);
      }
      setEmail("");
    } catch (err) {
      console.error(err);
      setStatus("error");
      toast.error(t.hero.cta_error);
    } finally {
      setLoading(false);
    }
  };

  const success = status === "success" || status === "already";

  return (
    <motion.form
      onSubmit={submit}
      data-testid="email-subscribe-form"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: 0.4 }}
      className="w-full max-w-xl mx-auto"
    >
      <div
        className="relative flex items-stretch glass glow-border glow-border-hover rounded-2xl p-1.5 transition-all"
        style={{ direction: dir }}
      >
        <input
          type="email"
          required
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (status !== "idle") setStatus("idle");
          }}
          placeholder={t.hero.cta_email}
          data-testid="email-input"
          className="flex-1 bg-transparent border-0 outline-none px-5 py-3.5 text-base text-[var(--text-main)] placeholder:text-[var(--text-muted)]"
          aria-label={t.hero.cta_email}
        />
        <button
          type="submit"
          disabled={loading || success}
          data-testid="email-submit-button"
          className="group inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-b from-[#ffd86b] via-[#d4af37] to-[#a07a1e] px-5 py-3 text-sm font-semibold tracking-wide text-[#0a0a0a] shadow-[0_8px_24px_-8px_rgba(212,175,55,0.6)] transition-all hover:shadow-[0_12px_36px_-8px_rgba(212,175,55,0.85)] hover:brightness-110 active:scale-[0.98] disabled:opacity-70 disabled:cursor-not-allowed"
        >
          {loading ? (
            <span className="inline-block h-4 w-4 rounded-full border-2 border-[#0a0a0a]/40 border-t-[#0a0a0a] animate-spin" />
          ) : success ? (
            <>
              <CheckCircle2 size={16} />
              <span>{status === "already" ? t.hero.cta_already : t.hero.cta_subscribed}</span>
            </>
          ) : (
            <>
              <span>{t.hero.cta_button}</span>
              <ArrowRight
                size={16}
                className={`transition-transform group-hover:translate-x-0.5 ${
                  dir === "rtl" ? "rotate-180 group-hover:-translate-x-0.5" : ""
                }`}
              />
            </>
          )}
        </button>
      </div>
      <div className="mt-3 flex items-center justify-center gap-2 text-xs text-[var(--text-muted)]">
        {status === "error" ? (
          <>
            <AlertCircle size={14} className="text-red-400" />
            <span data-testid="email-helper-text" className="text-red-300">
              {t.hero.cta_error}
            </span>
          </>
        ) : (
          <span data-testid="email-helper-text">{t.hero.cta_helper}</span>
        )}
      </div>
    </motion.form>
  );
};

export default EmailForm;
